import React, { useEffect, useMemo, useRef, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useFamilyData } from "../hooks/useFamilyData";

// cards
import TaskCard from "./cards/TaskCard";
import ApprovalsCard from "./cards/ApprovalsCard";
import HistoryCard from "./cards/HistoryCard";
import CalendarCard from "./cards/CalendarCard";
import AnalyticsCard from "./cards/AnalyticsCard";
import BankCard from "./cards/BankCard";
import RewardsCard from "./cards/RewardsCard";
import WeeklyRecapCard from "./cards/WeeklyRecapCard";
import GoalsCard from "./cards/GoalsCard";
import SettingsOverviewCard from "./cards/SettingsOverviewCard";
import AuditLogCard from "./cards/AuditLogCard";
import RepeatChoresCard from "./cards/RepeatChoresCard";
import SetupChecklistCard from "./cards/SetupChecklistCard";
import ClarificationsCard from "./cards/ClarificationsCard";

const MIN_CARD_WIDTH_SPAN = 1;
const MAX_CARD_WIDTH_SPAN = 4;
const MIN_CARD_HEIGHT_SPAN = 1;
const MAX_CARD_HEIGHT_SPAN = 6;
const RESIZE_STEP_PX = 90;
const DEFAULT_LAYOUT = [
  "setupChecklist",
  "tasks",
  "repeats",
  "approvals",
  "clarifications",
  "weeklyRecap",
  "calendar",
  "bank",
  "rewards",
  "goals",
  "analytics",
  "settingsOverview"
];
const PRIORITY_ORDER = ["tasks", "approvals", "clarifications"];
const DEFAULT_CARD_SIZES = {
  tasks: "large",
  approvals: "large",
  clarifications: "large"
};

export default function ParentDashboard({ familyId, familySettings, setView, canManage = false }) {
  const { tasks, history, kids } = useFamilyData(familyId, { canManage });
  const [cardOrder, setCardOrder] = useState([]);
  const [cardSizes, setCardSizes] = useState(DEFAULT_CARD_SIZES);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [resizingId, setResizingId] = useState(null);
  const [isEditingLayout, setIsEditingLayout] = useState(false);
  const draggingRef = useRef(null);
  const dragOverRef = useRef(null);
  const pendingOrderRef = useRef(null);
  const resizeRef = useRef(null);
  const cardSizesRef = useRef(cardSizes);
  const [maxColumns, setMaxColumns] = useState(() => {
    if (typeof window === "undefined") return 3;
    const width = window.innerWidth;
    if (width < 980) return 1;
    if (width < 1180) return 2;
    if (width < 1400) return 3;
    return 4;
  });

  const needsSetupChecklist = useMemo(() => {
    if (familySettings?.setupChecklistDismissed) return false;
    const hasKids = kids.length > 0;
    const hasTasks = tasks.length > 0;
    const hasApproval = history.some((item) => item.type === "approve");
    return !(hasKids && hasTasks && hasApproval);
  }, [familySettings?.setupChecklistDismissed, kids, tasks, history]);

  useEffect(() => {
    cardSizesRef.current = cardSizes;
  }, [cardSizes]);

  const visibilityMap = familySettings?.dashboardCardVisibility || {};
  const isCardVisible = (id) => {
    if (id === "setupChecklist") return true;
    return visibilityMap[id] !== false;
  };

  const clampWidthSpan = (value, columns = maxColumns) => {
    const maxWidth = Math.min(columns, MAX_CARD_WIDTH_SPAN);
    return Math.max(MIN_CARD_WIDTH_SPAN, Math.min(Number(value) || 1, maxWidth));
  };

  const clampHeightSpan = (value) =>
    Math.max(MIN_CARD_HEIGHT_SPAN, Math.min(Number(value) || 1, MAX_CARD_HEIGHT_SPAN));

  const normalizeSize = (size) => {
    if (!size) return { w: 1, h: 1, key: "standard" };
    if (typeof size === "string") {
      switch (size) {
        case "wide":
          return { w: clampWidthSpan(2), h: clampHeightSpan(1), key: "wide" };
        case "tall":
          return { w: clampWidthSpan(1), h: clampHeightSpan(2), key: "tall" };
        case "large":
          return { w: clampWidthSpan(2), h: clampHeightSpan(2), key: "large" };
        case "standard":
        default:
          return { w: clampWidthSpan(1), h: clampHeightSpan(1), key: "standard" };
      }
    }
    const w = Number(size.w) || 1;
    const h = Number(size.h) || 1;
    return {
      w: clampWidthSpan(w),
      h: clampHeightSpan(h),
      key: ""
    };
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleResize = () => {
      const width = window.innerWidth;
      const next =
        width < 980 ? 1 : width < 1180 ? 2 : width < 1400 ? 3 : 4;
      setMaxColumns(next);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const storedLayout =
      Array.isArray(familySettings?.dashboardLayout) && familySettings.dashboardLayout.length > 0
        ? familySettings.dashboardLayout
        : DEFAULT_LAYOUT;

    const normalized = [
      ...storedLayout.filter((id) => DEFAULT_LAYOUT.includes(id)),
      ...DEFAULT_LAYOUT.filter((id) => !storedLayout.includes(id))
    ];

    const layoutCustom = Boolean(familySettings?.layoutCustom);
    const ordered = layoutCustom
      ? normalized
      : [
          ...PRIORITY_ORDER.filter((id) => normalized.includes(id)),
          ...normalized.filter((id) => !PRIORITY_ORDER.includes(id))
        ];

    const withoutChecklist = ordered.filter((id) => id !== "setupChecklist");
    const withChecklist = needsSetupChecklist
      ? ["setupChecklist", ...withoutChecklist]
      : withoutChecklist;

    setCardOrder(withChecklist);
    setCardSizes({ ...DEFAULT_CARD_SIZES, ...(familySettings?.cardSizes || {}) });
  }, [
    familySettings?.dashboardLayout,
    familySettings?.cardSizes,
    familySettings?.layoutCustom,
    needsSetupChecklist
  ]);

  const saveLayout = async (nextOrder) => {
    if (!canManage) return;
    await updateDoc(doc(db, "families", familyId), {
      dashboardLayout: nextOrder,
      layoutCustom: true
    });
  };

  const saveSizes = async (nextSizes) => {
    if (!canManage) return;
    await updateDoc(doc(db, "families", familyId), {
      cardSizes: nextSizes,
      layoutCustom: true
    });
  };

  const applyGuidedLayout = async () => {
    if (!canManage) return;
    const normalized = [
      ...PRIORITY_ORDER.filter((id) => DEFAULT_LAYOUT.includes(id)),
      ...DEFAULT_LAYOUT.filter((id) => !PRIORITY_ORDER.includes(id))
    ];
    const withoutChecklist = normalized.filter((id) => id !== "setupChecklist");
    const guided = needsSetupChecklist
      ? ["setupChecklist", ...withoutChecklist]
      : withoutChecklist;
    setCardOrder(guided);
    setCardSizes(DEFAULT_CARD_SIZES);
    await updateDoc(doc(db, "families", familyId), {
      dashboardLayout: guided,
      cardSizes: DEFAULT_CARD_SIZES,
      layoutCustom: true
    });
  };

  const reorderCards = (list, fromId, toId) => {
    const next = list.filter((id) => id !== fromId);
    const toIndex = next.indexOf(toId);
    if (toIndex === -1) return list;
    next.splice(toIndex, 0, fromId);
    return next;
  };

  const handleDrop = (targetId) => (event) => {
    event.preventDefault();
    if (!canManage || !isEditingLayout) return;
    const activeId = draggingRef.current;
    if (!activeId || activeId === targetId) return;
    setCardOrder((prev) => {
      const next = reorderCards(prev, activeId, targetId);
      pendingOrderRef.current = next;
      return next;
    });
    draggingRef.current = null;
    dragOverRef.current = null;
    setDraggingId(null);
    setDragOverId(null);
    if (pendingOrderRef.current) {
      saveLayout(pendingOrderRef.current);
    }
    pendingOrderRef.current = null;
  };

  const handleDragStart = (id) => (event) => {
    if (!canManage || !isEditingLayout) return;
    draggingRef.current = id;
    pendingOrderRef.current = cardOrder;
    setDraggingId(id);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", id);
  };

  const handleDragEnd = () => {
    if (!isEditingLayout) return;
    if (pendingOrderRef.current) {
      saveLayout(pendingOrderRef.current);
    }
    draggingRef.current = null;
    dragOverRef.current = null;
    setDraggingId(null);
    setDragOverId(null);
    pendingOrderRef.current = null;
  };

  const handleDragOver = (targetId) => (event) => {
    if (!canManage || !isEditingLayout) return;
    event.preventDefault();
    const activeId = draggingRef.current;
    if (!activeId || activeId === targetId) return;
    if (dragOverRef.current === targetId) return;
    dragOverRef.current = targetId;
    setDragOverId(targetId);
    setCardOrder((prev) => {
      const next = reorderCards(prev, activeId, targetId);
      pendingOrderRef.current = next;
      return next;
    });
  };

  const handleResizeStart = (id) => (event) => {
    if (!canManage || !isEditingLayout) return;
    event.preventDefault();
    event.stopPropagation();
    const start = normalizeSize(cardSizesRef.current[id]);
    resizeRef.current = {
      id,
      startX: event.clientX,
      startY: event.clientY,
      startSpan: { w: start.w, h: start.h }
    };
    setResizingId(id);
    window.addEventListener("pointermove", handleResizeMove);
    window.addEventListener("pointerup", handleResizeEnd);
  };

  const handleResizeMove = (event) => {
    if (!resizeRef.current) return;
    const { id, startX, startY, startSpan } = resizeRef.current;
    const dx = event.clientX - startX;
    const dy = event.clientY - startY;
    const nextW = clampWidthSpan(startSpan.w + Math.round(dx / RESIZE_STEP_PX));
    const nextH = clampHeightSpan(startSpan.h + Math.round(dy / RESIZE_STEP_PX));
    setCardSizes((prev) => {
      const prevSize = normalizeSize(prev[id]);
      if (prevSize.w === nextW && prevSize.h === nextH) return prev;
      const next = { ...prev, [id]: { w: nextW, h: nextH } };
      cardSizesRef.current = next;
      return next;
    });
  };

  const handleResizeEnd = () => {
    if (!resizeRef.current) return;
    const { id, startSpan } = resizeRef.current;
    const currentSize = normalizeSize(cardSizesRef.current[id]);
    resizeRef.current = null;
    setResizingId(null);
    window.removeEventListener("pointermove", handleResizeMove);
    window.removeEventListener("pointerup", handleResizeEnd);
    if (startSpan.w !== currentSize.w || startSpan.h !== currentSize.h) {
      saveSizes(cardSizesRef.current);
    }
  };

  const renderCard = (id) => {
    switch (id) {
      case "tasks":
        return (
          <TaskCard
            familyId={familyId}
            isPro={familySettings.isPro}
            familySettings={familySettings}
            canManage={canManage}
          />
        );
      case "setupChecklist":
        if (!needsSetupChecklist) return null;
        return (
          <SetupChecklistCard
            familyId={familyId}
            familySettings={familySettings}
            setView={setView}
          />
        );
      case "repeats":
        return <RepeatChoresCard familyId={familyId} canManage={canManage} />;
      case "approvals":
        return (
          <ApprovalsCard
            familyId={familyId}
            isPro={familySettings.isPro}
            canManage={canManage}
          />
        );
      case "clarifications":
        return (
          <ClarificationsCard
            familyId={familyId}
            canManage={canManage}
            clarificationsEnabled={familySettings?.clarificationsEnabled}
          />
        );
      case "history":
        return <HistoryCard familyId={familyId} isPro={familySettings.isPro} />;
      case "weeklyRecap":
        return familySettings.isPro ? <WeeklyRecapCard familyId={familyId} /> : null;
      case "audit":
        return familySettings.isPro && canManage ? <AuditLogCard familyId={familyId} /> : null;
      case "calendar":
        return (
          <CalendarCard
            familyId={familyId}
            familySettings={familySettings}
            canManage={canManage}
          />
        );
      case "bank":
        return (
          <BankCard familyId={familyId} isPro={familySettings.isPro} canManage={canManage} />
        );
      case "settingsOverview":
        return <SettingsOverviewCard onOpenSettings={() => setView("settings")} />;
      case "goals":
        return familySettings.isPro && canManage ? (
          <GoalsCard familyId={familyId} familySettings={familySettings} canManage={canManage} />
        ) : null;
      case "rewards":
        return familySettings.isPro && canManage ? (
          <RewardsCard familyId={familyId} isPro={familySettings.isPro} />
        ) : null;
      case "analytics":
        return familySettings.isPro && canManage ? (
          <AnalyticsCard familyId={familyId} isPro={familySettings.isPro} />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <>
      {canManage && (
        <div className="layout-toolbar" data-tour="layout-toolbar">
          {isEditingLayout && (
            <div className="layout-hint">
              <span className="layout-snap-dot" />
              <span className="layout-hint-muted">Drag cards by the handle to reorder.</span>
            </div>
          )}
          <button
            className="btn-nav"
            title="Applies a recommended layout for Parent Hub."
            onClick={applyGuidedLayout}
          >
            Guided Layout
          </button>
          <button
            className="btn-nav success-btn"
            onClick={() => setIsEditingLayout((prev) => !prev)}
          >
            {isEditingLayout ? "Done" : "Edit Layout"}
          </button>
        </div>
      )}
      <div className="parent-grid parent-hub-grid">
      {cardOrder.map((id) => {
        const element = renderCard(id);
        if (!element || !isCardVisible(id)) return null;
        const size = normalizeSize(cardSizes[id]);
        const clampedWidth = Math.min(size.w, maxColumns);
        return (
          <div
            key={id}
            className={`parent-card-slot ${draggingId === id ? "is-dragging" : ""} ${dragOverId === id ? "is-drag-over" : ""} ${resizingId === id ? "is-resizing" : ""}`}
            style={{ gridColumnEnd: `span ${clampedWidth}`, gridRowEnd: `span ${size.h}` }}
            onDragOver={canManage ? handleDragOver(id) : undefined}
            onDrop={canManage ? handleDrop(id) : undefined}
          >
            {canManage && isEditingLayout && (
              <div className="card-controls" data-tour="card-controls">
                <button
                  className="card-drag-handle"
                  draggable
                  onDragStart={handleDragStart(id)}
                  onDragEnd={handleDragEnd}
                  title="Drag to reorder"
                >
                  ⋮⋮
                </button>
                <button
                  className="card-resize-handle"
                  onPointerDown={handleResizeStart(id)}
                  title="Drag to resize"
                />
              </div>
            )}
            {element}
          </div>
        );
      })}
      </div>
    </>
  );
}
